import { withTranslation } from "react-i18next";
import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual, isNil, isNull, isUndefined, size } from "lodash";
import { addDays } from "date-fns";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop"
import CircularProgress from "@mui/material/CircularProgress"
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";
import CloseIcon from '@mui/icons-material/Close';
import { green, grey, red } from '@mui/material/colors';
import DemandPriorityList from "@plannel/components/DemandPriorityList";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import runMpService from "@plannel/services/mp/run-mp-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import userService from "@plannel/services/system/user-service";

import { CreateButton, DeleteButton, ResultButton, RunButton, SettingsButton } from "@plannel/components/ActionIconButton";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import CreateVersionModal from "@plannel/components/modal/CreateVersionModal";
import VersionSettingsModal from "@plannel/components/modal/VersionSettingsModal";
import RunModal from "@plannel/components/modal/RunModal";
import PlanStrategyHelp from "@plannel/components/PlanStrategyHelp";

import CodeUtil from "@plannel/pages/utils/code-util";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { reloadMpVersionFilter } from "@plannel/components/filter/MpVersionFilter";
import { getLastBucketUnit, getPrimaryBucketUnit, normalizeBucketUnit } from "@plannel/pages/master-plan/utils/mp-bucket-util";

import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import planConfigService from "@plannel/services/data/plan-config-service";
import {HttpStatusCode} from "axios";
import { FS } from "@plannel/utils/font-util";

const ROLE_APP_DP = "ROLE_APP_DP";
const ROLE_APP_RP = "ROLE_APP_RP";
const DEMAND_PRIORITY_CD = "DEMAND_PRIORITY";

const getBucketLabelKey = bucket => {
  const normalizedBucket = normalizeBucketUnit(bucket);

  if (normalizedBucket === "day") {
    return "daily";
  }
  if (normalizedBucket === "week") {
    return "week";
  }
  if (normalizedBucket === "partialweek") {
    return "partialWeek";
  }
  if (normalizedBucket === "month") {
    return "monthly";
  }
  return bucket;
};

const formatPlanDateByBucket = (date, bucketUnit) => {
  if (!date) {
    return "";
  }

  const normalizedBucket = normalizeBucketUnit(bucketUnit);
  if (normalizedBucket === "month") {
    return dateUtils.formatDate(date, "yyyy-MM");
  }

  if (normalizedBucket === "week" || normalizedBucket === "partialweek") {
    const weekNo = getIsoWeekNumber(date);
    const weekLabel = weekNo < 10 ? `W0${weekNo}` : `W${weekNo}`;
    return `${dateUtils.formatDate(date, "yyyy-MM-dd")} ${weekLabel}`;
  }

  return dateUtils.formatDate(date, "yyyy-MM-dd");
};

const getIsoWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const getFrozenRangeUnitText = (bucketUnit, t) => {
  const normalizedBucket = normalizeBucketUnit(bucketUnit);

  if (normalizedBucket === "day") {
    return t("days");
  }

  if (normalizedBucket === "week" || normalizedBucket === "partialweek") {
    return t("weeks");
  }

  return t("months");
};

const formatFrozenRangeText = (frozenRange, bucketUnit, t) => {
  return `${frozenRange ?? 0} ${getFrozenRangeUnitText(bucketUnit, t)}`;
};

const { snackBarMessageColor } = ColorUtil;
const { versionStatusCode, module, buckets, limitVersion, mpPlanConfig: { SAFETY_STOCK_POLICY, KIT_PLAN_MODE, ALLOW_SEMI_ORDER }, itemType } = CodeUtil;
const { PRODUCTION_PRIORITY } = CodeUtil.priorityItems;
const { DAY } = buckets;

const { RELEASED, UNPLANNED, PLANNED, SCENARIO, SIMULATION, FAILED, RUNNING } = versionStatusCode;

// Run MP 요약(Plan Information / Plan Policy) 표시용 — 라벨 정렬 + 값 배지
const RUN_MP_LABEL_SX = { color: "text.secondary", fontSize: FS.TITLE, whiteSpace: "nowrap" };
const RUN_MP_VALUE_SX = { fontSize: FS.TITLE, fontWeight: 600, color: "text.primary" };
const BADGE_PALETTE = {
  teal: { bg: "rgba(4,139,168,0.12)", color: "#025B6E" },
  gray: { bg: "#F1EFE8", color: "#5F5E5A" },
  green: { bg: "#E1F5EE", color: "#0F6E56" },
  amber: { bg: "#FAEEDA", color: "#854F0B" },
  red: { bg: "#FCEBEB", color: "#A32D2D" },
};
const ValueBadge = ({ children, tone = "teal" }) => {
  const p = BADGE_PALETTE[tone] ?? BADGE_PALETTE.teal;
  return (
    <Box component="span" sx={{ display: "inline-block", fontSize: FS.SUB, fontWeight: 600, px: 1.25, py: "2px", borderRadius: 3, bgcolor: p.bg, color: p.color, lineHeight: 1.6 }}>
      {children}
    </Box>
  );
};
// 버전 상태 → 배지 색
const statusTone = (s) => (s === PLANNED ? "amber" : s === RELEASED ? "gray" : s === FAILED ? "red" : "teal");
// Run MP 요약 섹션 셸 (카드 테두리·배경 없이 3분할 컬럼만)
const RUN_MP_CARD_SX = { minWidth: 0 };
const RUN_MP_DIVIDER_SX = { mt: 1, mb: 2, backgroundColor: "primary.main", borderColor: "primary.main", borderWidth: 1 };
const { MP, DP, RP } = module;

const now = new ZDate();

const RunningIcon = (path) => {
  const style = {};
  if (isEqual(path, "title")) {
    style.ml = 1;
  } else {
    style.mr = 1;
  }

  return <CircularProgress color={"info"} size={"17px"} sx={style} />;
}

const RunMP = ({ t, viewName, title }) => {
  const user = JSON.parse(localStorage.getItem("user"));

  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const [userRoles, setUserRoles] = useState(reduxViewState?.userRoles ?? []);
  const [dpVersions, setDpVersions] = useState([]);
  const [rpVersions, setRpVersions] = useState([]);

  const [versionList, setVersionList] = useState([]);
  const [selectVersion, setSelectVersion] = useState(reduxViewState?.version ?? null);
  const [verStartDate, setVerStartDate] = useState();
  const [verEndDate, setVerEndDate] = useState();

  const { DATE_PRIORITY: { DUE_DATE }, DEMAND_PRIORITY } = CodeUtil.priorityItems;
  const { datePriorityNameByCode } = CodeUtil;

  const [demandPriority, setDemandPriority] = useState([]);
  const [defaultDemandPriority, setDefaultDemandPriority] = useState([]);
  const [defaultDatePriority, setDefaultDatePriority] = useState([]);

  const defaultConfig = {
    bucket: DAY,
    range: 90,
    start: now,
    end: addDays(now, 90),
    resourceCapacity: "finite",
    materialConst: "masterData",
    frozenRange: 0,
    planStrategy: "forward",
    backorder: "allow",
    safetyStockPolicy: "NONE",
    kitPlanMode: false,
    allowSemiOrder: false,
    altResPolicy: PRODUCTION_PRIORITY.FAST,
    enableRollingPlan: false
  }

  const configDefaultValue = useMemo(() => {
    return {
      ...defaultConfig,
      demandPriority: defaultDemandPriority,
      datePriority: defaultDatePriority
    };
   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDemandPriority, defaultDatePriority]);

  const mpSettingValue = useRef(defaultConfig);
  const [resourceCapacity, setResourceCapacity] = useState(configDefaultValue.resourceCapacity);
  const [materialConst, setMaterialConst] = useState(configDefaultValue.materialConst);
  const [planStrategy, setPlanStrategy] = useState(configDefaultValue.planStrategy);
  const [backorder, setBackorder] = useState(configDefaultValue.backorder);
  const [frozenRange, setFrozenRange] = useState(configDefaultValue.frozenRange);
  const [safetyStockPolicy, setSafetyStockPolicy] = useState(configDefaultValue.safetyStockPolicy);
  const [kitPlanMode, setKitPlanMode] = useState(configDefaultValue.kitPlanMode);
  const [allowSemiOrder, setAllowSemiOrder] = useState(configDefaultValue.allowSemiOrder);
  const [altResPolicy, setAltResPolicy] = useState(configDefaultValue.altResPolicy);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);
  const [loading, setLoading] = useState(false);

  const [createNewModalOpen, setCreateNewModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);

  const [chartData, setChartData] = useState([]);
  const chartSeries = [
    { id: "demandQty", field: "demandQty", text: t("demandNumber"), type: "line", typeOptions: { fill: ColorUtil.colorSet(0), strokeWidth: 3 }, groupName: t("invQty") },
    { id: "supplyQty", field: "supplyQty", text: t("supply"), type: "line", typeOptions: { fill: ColorUtil.colorSet(1), strokeWidth: 3 }, groupName: t("invQty") },
    { id: "productionQty", field: "productionQty", text: t("production"), type: "bar", typeOptions: { fill: ColorUtil.colorSet(2), strokeWidth: 3 }, groupName: t("invQty") },
    { id: "onHandQty", field: "onHandQty", text: t("onhand"), type: "bar", typeOptions: { fill: ColorUtil.colorSet(3), strokeWidth: 3 }, groupName: t("invQty") }
  ];

  const retrieveInventory = () => {
    const params = {};
    if (selectVersion?.id) {
      params.versionId = selectVersion.id;
    }

    runMpService.getInventory(params)
      .then(response => {
        const { results } = response.data;
        if (results) {
          setChartData(results);
        } else {
          setChartData([]);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const getGridDataByDemandPriorty = (demandPrioirtyObj) => {
    const gridData = [];

    if (size(demandPrioirtyObj) > 0) {
      Object.keys(demandPrioirtyObj).forEach(key => {
        const seq = demandPrioirtyObj[key];
        if (seq !== 99) {
          gridData.push({
            priorityItem: key,
            priorityItemName: DEMAND_PRIORITY?.[key]?.name,
            sorting: DEMAND_PRIORITY?.[key]?.sorting,
            seq
          })
        }
      });

      gridData.sort((a, b) => {
        return a?.seq < b?.seq? -1 : 1;
      });
    }

    return gridData;
  }

  const makeDemandPriorityGridData = (resData) => {
    if (resData?.DATE_PRIORITY) {
      setDefaultDatePriority(resData?.DATE_PRIORITY);
    }

    const demandPriorityConfig = getGridDataByDemandPriorty(resData?.DEMAND_PRIORITY);
    const updatedData = demandPriorityConfig.map(obj => ({
      ...obj,
      seq: obj.seq + 1
    }));

    const totalDemandPrioirty = [
      {
        priorityItem: resData?.DATE_PRIORITY,
        priorityItemName: datePriorityNameByCode[resData?.DATE_PRIORITY],
        sorting: "asc",
        seq: 1
      },
      ...updatedData];

    if (resData?.DEMAND_PRIORITY) {
      setDefaultDemandPriority(totalDemandPrioirty);
    }

  }

  const retrievePlanConfig = () => {
    const param = {
      moduleCd: MP
    };

   let hasDemandPriority = false;

    planConfigService.getAll(param)
      .then((response) => {
        const { data } = response;

        for (const item of data.results) {
          if (item.configCd === DEMAND_PRIORITY_CD) {
            hasDemandPriority = true;
            const resData = item?.objVal;
            if (resData) {
              makeDemandPriorityGridData(resData);
            }
          } else if (item.configCd === "MP_SETTINGS_CONFIG") {
            const { objVal } = item;
            // const data = {...configDefaultValue};
            data.TIME_BUCKETS = objVal?.TIME_BUCKETS;
            data.bucket = objVal?.TIME_BUCKETS?.[0]?.UNIT;
            data.range = objVal?.PLANNING_RANGE;
            data.backorder = objVal?.BACKORDER;
            data.planStrategy = objVal?.PS;
            data.resourceCapacity = objVal?.RC;
            data.materialConst = objVal?.MC;
            data.safetyStockPolicy = objVal?.SAFETY_STOCK_POLICY ?? "NONE";
            data.kitPlanMode = objVal?.KIT_PLAN_MODE;
            data.allowSemiOrder = objVal?.ALLOW_SEMI_ORDER;
            data.frozenRange = objVal?.FROZEN_RANGE;
            data.altResPolicy = objVal?.ALT_RES_POLICY;
            data.enableRollingPlan = objVal?.ENABLE_ROLLING_PLAN ?? false;

            setPlanStrategy(objVal?.PS);
            setMaterialConst(objVal?.MC);
            setResourceCapacity(objVal?.RC);

            mpSettingValue.current = data;
          }
        }
      }).catch(e => {
        debug(e);
      }).then(() => {
      if (!hasDemandPriority) {
        setDefaultDatePriority(DUE_DATE);

        const totalDemandPrioirty = [
          {
            priorityItem: DUE_DATE,
            priorityItemName: datePriorityNameByCode[DUE_DATE],
            sorting: "asc",
            seq: 1
          }
        ]

        setDefaultDemandPriority(totalDemandPrioirty);
      }
    });
  };


  useEffect(() => {
    if (!isEmpty(selectVersion?.planConfigObj.DEMAND_PRIORITY)) {
      setDemandPriority(selectVersion?.planConfigObj.DEMAND_PRIORITY);
    } else {
      setDemandPriority([]);
    }
  }, [selectVersion]);

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: user.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  const sortMpVersions = versions => {
    const basicVersions = [];
    const executedVersions = [];
    const releaseVersions = [];

    for (const ver of versions) {
      const { statusCd, execTs } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        if (isNull(execTs)) {
          basicVersions.push(ver);
        } else {
          executedVersions.push(ver);
        }
      }
    }
    const list = [...basicVersions, ...executedVersions, ...releaseVersions];
    if (size(list) > limitVersion) {
      return list.slice(0, limitVersion);
    }
    return list;
  };

  const retrieveMpVersion = receivedVersion => {
    const params = { moduleCd: MP, statusCds: [UNPLANNED, PLANNED, FAILED, RUNNING, RELEASED], orderByColumn: "updatedTs", sortType: "desc" };
    planVersionService.getAll(params)
      .then(response => {
        const { results } = response.data;
        if (results && !isEmpty(results)) {
          const sortedVersions = sortMpVersions(results);
          setVersionList(sortedVersions);

          if (receivedVersion) {
            const currentVersion = results.find(result => isEqual(result.id, receivedVersion.id));
            currentVersion ? setSelectVersion(currentVersion) : setSelectVersion(results[0]);
          } else {
            if (reduxViewState?.version) {
              const reduxVersion = results.find(result => isEqual(result.id, reduxViewState.version.id));
              if (!isUndefined(reduxVersion)) {
                setSelectVersion(reduxVersion);
              } else {
                setSelectVersion(results[0]);
              }
            } else {
              setSelectVersion(results[0]);
            }
          }
        } else {
          retrieveInventory();
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const sortDpVersions = versions => {
    const releaseVersions = [];
    const scenarioVersions = [];

    for (const ver of versions) {
      const { statusCd } = ver;

      if (isEqual(statusCd, RELEASED)) {
        releaseVersions.push(ver);
      } else {
        scenarioVersions.push(ver);
      }
    }

    return [...releaseVersions, ...scenarioVersions];
  }

  const retrieveDpVersion = () => {
    planVersionService.getAll({ moduleCd: DP, statusCds: [RELEASED, SCENARIO], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        setDpVersions(sortDpVersions(results));
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveRpVersion = () => {
    planVersionService.getAll({ moduleCd: RP, statusCds: [RELEASED, SIMULATION, PLANNED], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        const runSimulationVersion = !isEmpty(results) ? results.filter(data => !isEmpty(data.execTs)) : [];

        setRpVersions(runSimulationVersion);
      })
      .catch(e => {
        debug(e);
      });
  };

  const setPlanPolicy = (version, planPolicy) => {
    const { planConfigObj } = version;
    planConfigObj.RC = planPolicy.resourceCapacity;
    planConfigObj.MC = planPolicy.materialConst;
    planConfigObj.PS = planPolicy.planStrategy;
    planConfigObj.BACKORDER = planPolicy.backorder;
    planConfigObj.FROZEN_RANGE = planPolicy.frozenRange;
    planConfigObj.SAFETY_STOCK_POLICY = planPolicy.safetyStockPolicy;
    planConfigObj.KIT_PLAN_MODE = planPolicy.kitPlanMode;
    planConfigObj.ALLOW_SEMI_ORDER = planPolicy.allowSemiOrder;
    planConfigObj.ALT_RES_POLICY = planPolicy.altResPolicy;

    version.planConfigObj = planConfigObj;
  }

  const createNewVersion = (params) => {
    setProgressOn(true);
    planVersionService.upsert(params)
      .then(response => {
        if (response.status === HttpStatusCode.NoContent) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DuplicateVersion") });
          return;
        }
        if (response.data) {
          retrieveMpVersion(response.data);
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        }
      })
      .catch(e => {
        const { status } = e.response;
        if (status === 409) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: selectVersion.name }) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        }
        debug(e);
      })
      .then(() => {
        setProgressOn(false);
      });
  }

  const removeVersion = () => {
    setProgressOn(true);
    planVersionService.deleteVersionWithData([selectVersion.id])
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        retrieveMpVersion();
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        debug(e);
      })
      .then(() => {
        setProgressOn(false);
      });
  };

  const runMp = () => {
    setProgressOn(true);
    runMpService.run(selectVersion)
      .then(response => {
        if (response.status === 200) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_MpSuccess") });
        }
      })
      .catch(e => {
        const { status } = e.response;
        if (status === 409) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: selectVersion.name }) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_MpFailed") });
        }
        debug(e);
      })
      .finally(() => {
        reloadMpVersionFilter(reduxDispatch);
        retrieveMpVersion(selectVersion);
        setProgressOn(false);
      });
  }

  // const hasDpVersion = () => {
  //   const dpVersionId = selectVersion?.parent?.id;
  //
  //   if (dpVersionId) {
  //     const dpVersionIds = dpVersions.map(ver => ver.id);
  //     if (!isEmpty(dpVersionIds)) {
  //       return dpVersionIds.indexOf(dpVersionId) > -1;
  //     }
  //   }
  //
  //   return false;
  // }

  const isModifiedPolicy = version => {
    const { planConfigObj } = version;

    return !isEqual(planConfigObj?.RC, resourceCapacity) || !isEqual(planConfigObj?.MC, materialConst)
     || !isEqual(planConfigObj?.PS, planStrategy) || !isEqual(planConfigObj?.BACKORDER, backorder)
     || !isEqual(planConfigObj?.SAFETY_STOCK_POLICY, safetyStockPolicy) || !isEqual(planConfigObj?.KIT_PLAN_MODE, kitPlanMode)
    || !isEqual(planConfigObj?.ALLOW_SEMI_ORDER, allowSemiOrder) || !isEqual(planConfigObj?.ALT_RES_POLICY, altResPolicy);
  }

  const handleModalOpen = (modalName) => {
    if (modalName === "CREATE") {
      setCreateNewModalOpen(true);
    } else if (modalName === "CONFIG") {
      setConfigModalOpen(true);
    } else if (modalName === "RUN") {
      setDialogInfo({ open: true, title: "confirm", content: t(isModifiedPolicy(selectVersion) ? "MSG_ConfirmSaveAndRun" : "MSG_RunMp", { target: t("planPolicy") }) })
      // if (hasDpVersion()) {
      //   if (isModifiedPolicy(selectVersion)) {
      //     setDialogInfo({ open: true, title: "confirm", content: t("MSG_ConfirmSaveAndRun", { target: t("planPolicy") }) })
      //   } else {
      //     setRunModalOpen(true);
      //   }
      // } else {
      //   setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotExistConfiguredDpVersion") });
      //   setConfigModalOpen(true);
      // }
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      setPlanPolicy(selectVersion, { resourceCapacity, materialConst, planStrategy, backorder, safetyStockPolicy, kitPlanMode, allowSemiOrder, altResPolicy });
      createNewVersion(selectVersion);
      reloadMpVersionFilter(reduxDispatch);
    } else if (action === "DELETE") {
      removeVersion();
      reloadMpVersionFilter(reduxDispatch);
    } else if (action === "CONFIRM") {
      setPlanPolicy(selectVersion, { resourceCapacity, materialConst, planStrategy, backorder, safetyStockPolicy, kitPlanMode, allowSemiOrder, altResPolicy });
      runMp();
    }
  }

  const openMpResult = () => {
    const reduxKey = "MP_PLAN_REVIEW";

    const viewState = reduxUtil.createObjectState(reduxKey, { mpVersion: selectVersion, currentPage: 0, pageSize: 10 });
    reduxDispatch(reduxUtil.updateViewState(viewState));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }


  const primaryBucketUnit = getPrimaryBucketUnit(selectVersion?.planConfigObj);
  const lastBucketUnit = getLastBucketUnit(selectVersion?.planConfigObj);
  const formattedPlanStartDate = formatPlanDateByBucket(verStartDate, primaryBucketUnit);
  const formattedPlanEndDate = formatPlanDateByBucket(verEndDate, lastBucketUnit);
  const frozenRangeText = formatFrozenRangeText(frozenRange, primaryBucketUnit, t);

  useEffect(() => {
    if (!isEmpty(selectVersion)) {
      const { PLAN_START_DATE, PLAN_END_DATE, RC, MC, PS, BACKORDER, FROZEN_RANGE, SAFETY_STOCK_POLICY, KIT_PLAN_MODE, ALLOW_SEMI_ORDER, ALT_RES_POLICY } = selectVersion.planConfigObj;
      setVerStartDate(PLAN_START_DATE);
      setVerEndDate(PLAN_END_DATE);
      setResourceCapacity(RC);
      setMaterialConst(MC);
      setPlanStrategy(PS);
      setBackorder(BACKORDER);
      setFrozenRange(FROZEN_RANGE ?? 0);
      setSafetyStockPolicy(SAFETY_STOCK_POLICY ?? "NONE");
      setKitPlanMode(KIT_PLAN_MODE);
      setAllowSemiOrder(ALLOW_SEMI_ORDER);
      setAltResPolicy(ALT_RES_POLICY);

      const reduxState = reduxUtil.createObjectState(viewName, { version: selectVersion, userRoles });
      reduxDispatch(reduxUtil.updateViewState(reduxState));

      retrieveInventory();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectVersion]);

  useEffect(() => {
    if (loading && userRoles) {
      retrieveMpVersion();
      if (userRoles.includes(ROLE_APP_DP)) {
        retrieveDpVersion();
      }

      if (userRoles.includes(ROLE_APP_RP)) {
        retrieveRpVersion();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, userRoles]);

  useEffect(() => {
    retrievePlanConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles])

  useEffect(() => {
    setLoading(true);
    if (isEmpty(userRoles)) {
      retrieveUserRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const iconColor = planVersion => {
    if (planVersion) {
      const { statusCd } = planVersion;
      if (isEqual(statusCd, RELEASED)) {
        return grey[900];
      } else if (isEqual(statusCd, PLANNED)) {
        return green[600];
      } else if (isEqual(statusCd, RUNNING)) {
        return green[600];
      }

      return red[600];
    }

    return null;
  };
  const getExecTs = version => {
    return version?.execTs ? `(${dateUtils.formatDateTime(version.execTs)})` : "";
  }

  const isBlankVersion = isEmpty(selectVersion) || isNull(selectVersion);
  const isReleasedVersion = isEqual(selectVersion?.statusCd, RELEASED);
  const isRunningVersion = isEqual(selectVersion?.statusCd, RUNNING);
  const isFailedVersion = isEqual(selectVersion?.statusCd, FAILED);

  const setToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE, PLAN_END_DATE } = version.planConfigObj;

      return `${PLAN_START_DATE} ~ ${PLAN_END_DATE}`
    }

    return null;
  }

  const iconHandler = (statusCd, path) => {
    if (isEqual(statusCd, RELEASED)) {
      return GavelIcon;
    } else if (isEqual(statusCd, RUNNING)) {
      return () => RunningIcon(path);
    } else if (isEqual(statusCd, FAILED)) {
      return CloseIcon;
    } else {
      return ViewInArIcon;
    }
  }


  return (
    <>
        <FilterContainer>
          <Box>
            <VersionAutocomplete
              options={versionList}
              value={selectVersion}
              onChange={(_, newValue) => setSelectVersion(newValue)}
              label={t("version")}
              getIcon={(option) => iconHandler(option.statusCd)}
              getIconColor={iconColor}
              getTooltip={setToolTip}
              minWidth={250}
              inputIconSx={{ ml: 1 }}
            />
          </Box>
          <Box sx={{ ml: "auto", mr: 1.5, minWidth: 160 }}>
            {/* 계획 검토 화면이 권한 밖인 테넌트에서는 결과 버튼을 내보내지 않는다 */}
            {isPermitted("MP_PLAN_REVIEW") && (
              <ResultButton disabled={isBlankVersion || isRunningVersion || isFailedVersion || isEqual(selectVersion.statusCd, UNPLANNED)} onClick={openMpResult} />
            )}
            <CreateButton onClick={() => handleModalOpen("CREATE")} />
            <DeleteButton disabled={isBlankVersion || isReleasedVersion || isRunningVersion}
              onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_VersionPermanentlyDelete", { version: selectVersion?.name }) })} />
            <RunButton disabled={isBlankVersion || isReleasedVersion || isRunningVersion} onClick={() => handleModalOpen("RUN")} />
            <SettingsButton label="settings" disabled={isBlankVersion || isRunningVersion} onClick={() => handleModalOpen("CONFIG")} />
          </Box>
        </FilterContainer>
        <SplitterContainer initialSizes={[1, 1.4]}>
          <Box sx={{ height: "100%" }}>
            <Box id="run-mp-typo" className="ag-theme-balham grid-area non-background-selected-row"  sx={{ mt: 1, p: 2, height: "97%", overflow: "auto", display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1.15fr" }, gap: 2, alignItems: "start" }}>
              {/* 카드 1: Plan Information */}
              <Box sx={RUN_MP_CARD_SX}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>{t("planInfo")}</Typography>
                <Divider sx={RUN_MP_DIVIDER_SX} />
                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2.5, rowGap: 1.25, alignItems: "center" }}>
                  <Typography sx={RUN_MP_LABEL_SX}>{t("descTxt")}</Typography>
                  <Typography sx={RUN_MP_VALUE_SX}>{selectVersion?.descTxt || "-"}</Typography>
                  <Typography sx={RUN_MP_LABEL_SX}>{t("planningBucket")}</Typography>
                  <Box>{primaryBucketUnit ? <ValueBadge>{t(getBucketLabelKey(primaryBucketUnit))}</ValueBadge> : "-"}</Box>
                  <Typography sx={RUN_MP_LABEL_SX}>{t("planHorizon")}</Typography>
                  <Typography sx={RUN_MP_VALUE_SX}>{formattedPlanStartDate} ~ {formattedPlanEndDate}</Typography>
                  <Typography sx={RUN_MP_LABEL_SX}>{t("frozenRangeSim")}</Typography>
                  <Typography sx={RUN_MP_VALUE_SX}>{frozenRangeText}</Typography>
                  <Typography sx={RUN_MP_LABEL_SX}>{t("planStatus")}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <ValueBadge tone={statusTone(selectVersion?.statusCd)}>{CodeUtil.versionStatusLabel(selectVersion?.statusCd)}</ValueBadge>
                    <Typography component="span" sx={{ color: "text.disabled", fontSize: FS.BODY }}>{getExecTs(selectVersion)}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* 카드 2: Demand Priority */}
              <Box sx={RUN_MP_CARD_SX}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>{t("demandPriority")}</Typography>
                <Divider sx={RUN_MP_DIVIDER_SX} />
                <DemandPriorityList
                  items={isEmpty(selectVersion) ? defaultDemandPriority : demandPriority}
                  keyField="priorityItemName"
                  t={t}
                />
              </Box>

              {/* 카드 3: Plan Policy */}
              <Box sx={RUN_MP_CARD_SX}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>{t("planPolicy")}</Typography>
                  <PlanStrategyHelp />
                </Box>
                <Divider sx={RUN_MP_DIVIDER_SX} />
                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 3, rowGap: 1.5, alignItems: "center" }}>
                  <Typography sx={RUN_MP_LABEL_SX}>{t("resourceCapa")}</Typography>
                  <Box><ValueBadge>{t(resourceCapacity) ?? ""}</ValueBadge></Box>
                  <Typography sx={RUN_MP_LABEL_SX}>{t("materialConst")}</Typography>
                  <Box><ValueBadge>{t(materialConst) ?? ""}</ValueBadge></Box>
                  <Typography sx={RUN_MP_LABEL_SX}>{t("planStrategy")}</Typography>
                  {/* forward2 는 내부 저장값일 뿐 — 화면에서는 포워드 + 계획 기준(가용 시점 기준=forward / 납기 기준=forward2)으로 보인다 */}
                  <Box><ValueBadge>{t(planStrategy === "forward2" ? "forward" : planStrategy) ?? ""}</ValueBadge></Box>
                  {(planStrategy === "forward" || planStrategy === "forward2") && (
                    <>
                      <Typography sx={RUN_MP_LABEL_SX}>{t("planningBasis")}</Typography>
                      <Box><ValueBadge>{t(planStrategy === "forward2" ? "planningBasisDueDatePullIn" : "planningBasisEarliestAvailable")}</ValueBadge></Box>
                    </>
                  )}
                  <Typography sx={RUN_MP_LABEL_SX}>{t("backorder")}</Typography>
                  <Box><ValueBadge tone={backorder === "allow" ? "green" : "gray"}>{t(backorder) ?? ""}</ValueBadge></Box>
                  <Typography sx={RUN_MP_LABEL_SX}>{t(SAFETY_STOCK_POLICY.name)}</Typography>
                  <Box><ValueBadge tone={safetyStockPolicy && safetyStockPolicy !== "NONE" ? "teal" : "gray"}>{t(safetyStockPolicy === "TargetStock" ? "targetStock" : safetyStockPolicy === "MinMax" ? "minMax" : "notUse")}</ValueBadge></Box>
                  <Typography sx={RUN_MP_LABEL_SX}>{t(ALLOW_SEMI_ORDER.name)}</Typography>
                  <Box><ValueBadge tone={allowSemiOrder ? "green" : "gray"}>{allowSemiOrder ? t("allow") : t("notAllow")}</ValueBadge></Box>
                  <Typography sx={RUN_MP_LABEL_SX}>{t("altResPolicy")}</Typography>
                  <Box><ValueBadge>{!isNil(altResPolicy) ? t(altResPolicy) : t(PRODUCTION_PRIORITY.FAST)}</ValueBadge></Box>
                  {!isEmpty(userRoles) && userRoles.includes(ROLE_APP_RP) && (
                    <>
                      <Typography sx={RUN_MP_LABEL_SX}>{t(KIT_PLAN_MODE.name)}</Typography>
                      <Box><ValueBadge>{kitPlanMode ? t(itemType.KIT.name) : t(itemType.FG.name)}</ValueBadge></Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>

          </Box>
        <Box id={"run-mp-chart-area"} className="chart-grid-area chart-area" sx={{ height: "95%", mt: 1, mx: 2 }}>
          <ChartComposed
            xAxis={"hrchyName"}
            dataSeries={chartSeries}
            data={chartData}
            nullConvert
          />
        </Box>
      </SplitterContainer>
      <CreateVersionModal
        viewName={viewName}
        width={500}
        moduleCd={MP}
        modalOpen={createNewModalOpen}
        setModalOpen={setCreateNewModalOpen}
        defaultValue={{...configDefaultValue, ...mpSettingValue.current}}
        planVersions={versionList}
        createVersion={createNewVersion}
        dpVersions={dpVersions}
        rpVersions={rpVersions}
        iconColor={iconColor}
        setSnackInfo={setSnackInfo}
        useModules={userRoles}
        planConfigObj={mpSettingValue.current}
      />
      <VersionSettingsModal
        viewName={viewName}
        moduleCd={MP}
        modalOpen={configModalOpen}
        setModalOpen={setConfigModalOpen}
        defaultValue={{...configDefaultValue, ...mpSettingValue.current}}
        selectedPlanVersion={selectVersion}
        dpVersions={dpVersions}
        rpVersions={rpVersions}
        retrievePlanVersion={retrieveMpVersion}
        setSnackInfo={setSnackInfo}
        setProgressOn={setProgressOn}
        useModules={userRoles}
      />
      <RunModal
        title={"runMasterPlan"}
        modalOpen={runModalOpen}
        setModalOpen={setRunModalOpen}
        selectedPlanVersion={selectVersion}
        run={runMp}
      />
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
        duration={snackInfo.duration}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </>
  )
}

export default withTranslation()(RunMP);
